/**
 * Default Language and Currency Settings
 * 默认语言和货币配置
 *
 * These are the built-in default configurations for the mobile system.
 * API data can override or merge with these defaults.
 * 这些是 mobile 系统的内置默认配置。
 * API 数据可以覆盖或合并这些默认值。
 */

// ==================== Language Settings / 语言设置 ====================

/**
 * Default supported languages / 默认支持的语言
 *
 * By default, only Australian English is enabled.
 * API can override this to enable multiple languages.
 * 默认只启用澳大利亚英语。
 * API 可以覆盖此设置以启用多种语言。
 */
export const DEFAULT_SUPPORTED_LANGUAGES = [{ code: 'en', name: 'English', flag: '🇦🇺', countryCode: 'au' }]

/**
 * All available languages with translations / 所有可用的语言及其翻译
 *
 * This is the complete list of languages that have UI translations.
 * API can select which ones to enable for a specific venue.
 * 这是所有拥有 UI 翻译的语言完整列表。
 * API 可以选择为特定餐厅启用哪些语言。
 */
export const ALL_AVAILABLE_LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇦🇺', countryCode: 'au' },
  { code: 'fr', name: 'Français', flag: '🇫🇷', countryCode: 'fr' },
  { code: 'zh', name: '中文', flag: '🇨🇳', countryCode: 'cn' },
]

/**
 * Default UI translations for all supported languages
 * 所有支持语言的默认 UI 翻译
 */
export const DEFAULT_UI_TRANSLATIONS = {
  // English translations / 英文翻译
  en: {
    // Menu Page / 菜单页面
    menu: 'Menu',
    sold_out: 'SOLD OUT',
    popular: 'Popular',
    cancel_order: 'CANCEL ORDER',

    // Cart Page / 购物车页面
    your_order: 'Your order',
    subtotal: 'Subtotal',
    discount: 'Discount',
    order_total: 'Order total',
    back_to_menu: 'BACK TO MENU',
    have_discount_code: 'HAVE A DISCOUNT CODE?',
    enter_discount_code: 'Enter discount code',
    invalid_discount_code: 'Invalid discount code',
    discount_applied: 'Discount has been applied',
    public_holiday_surcharge: 'Public holiday surcharge',
    weekday_surcharge: '{weekDay} surcharge',
    weekdays: {
      monday: 'Monday',
      tuesday: 'Tuesday',
      wednesday: 'Wednesday',
      thursday: 'Thursday',
      friday: 'Friday',
      saturday: 'Saturday',
      sunday: 'Sunday'
    },
    payment_surcharge: 'Venue Surcharge',
    enter_name_prompt: 'Enter your {nameType} to be called when your order is ready',
    edit: 'Edit',
    remove: 'REMOVE',
    add_label: 'Add:',
    remove_label: 'Remove:',
    add_something_extra: 'Add something extra:',
    cart: 'Cart',
    checkout: 'Checkout',
    empty_cart: 'Your cart is empty',
    continue_shopping: 'Continue Shopping',
    // Currency estimate warning / 货币估算警告
    currency_estimate_warning: 'Prices shown in {currency} are an estimate only.',
    currency_charge_notice: 'You\'ll be charged in {defaultCurrency}. Your bank may apply a different exchange rate and fees.',

    // Age Verification / 年龄验证
    age_verification: 'Age verification',
    age_info:
      'Your order contains Alcohol. Please confirm that you are 18 or older. You may be required to show ID to the venue.',
    age_ask_text: 'Yes, I\'m 18 or older',
    wrong_age_text:
      'You need to be 18 or older to order products that contain alcohol. Please remove these products from your cart before continuing.',

    // Payment Page / 支付页面
    payment: 'Payment',
    payment_method: 'Payment Method',
    connecting_to: 'Connecting to',
    payment_terminal: 'payment terminal',
    please_follow_the: 'Please follow the',
    payment_instructions_on: 'payment instructions on',
    the_terminal_below: 'the terminal below',
    cancel_payment: 'CANCEL PAYMENT',
    payment_failed: 'Payment failed',
    retry: 'RETRY',
    cancel_payment_title: 'Cancel payment?',
    cancel_payment_confirm: 'Are you sure you want to cancel your payment?',
    failed_to_generate_order: 'Failed to generate order',
    please_resubmit_order: 'Please resubmit the order or contact the administrator',
    resubmit: 'RESUBMIT',

    // Order Received Page / 订单确认页面
    thank_you: 'Thank you!',
    order_received: 'We have received your order',
    status: 'Status:',
    order_details: 'Order details:',
    order_number: 'ORDER NUMBER',
    store_order_number: 'Order number',
    show_each_store_note: 'Show each store its own number when you collect.',
    order_placed: 'Order placed:',
    table_zone: 'Table/zone:',
    total_amount_paid: 'Total amount paid:',
    questions_about_order: 'Questions about your order?',
    scan_for_receipt: 'Scan here for your receipt',
    sms_receipt: 'SMS RECEIPT',
    email_receipt: 'EMAIL RECEIPT',
    total_inc_gst: 'Total (inc GST)',
    close: 'CLOSE',
    enter_phone_number: 'Enter local phone number',
    enter_email_address: 'Enter email address',

    // Product Detail Page / 产品详情页面
    item_added_to: 'Item added to',
    required: 'Required',
    // 组头那句「要选几样」后面拼的「必选」标记。**必须与 *_required 那几句的标点逐字一致**：
    // 以前这里是代码拼的 ` (${t('required')})` —— 半角空格 + 半角括号，而中文包的
    // `select_at_least_n_required` 用的是全角（必选）。于是中文门店同一个组，组头念
    // `至少选择 2 项 (必选)`、红字念 `至少选择 2 项（必选）`，两种标点。
    // 前导空格是句子的一部分，不是排版：中文不带空格。
    required_suffix: ' (Required)',
    optional: 'Optional',
    select_n: 'Select {n}',
    select_n_required: 'Select {n} (Required)',
    select_between_n_m: 'Select between {n} and {m}',
    select_between_n_m_required: 'Select between {n} and {m} (Required)',
    select_at_least_n: 'Select at least {n}',
    select_at_least_n_required: 'Select at least {n} (Required)',
    select_up_to_n: 'Select up to {n}',
    select_up_to_n_required: 'Select up to {n} (Required)',

    // Footer Bar Buttons / 底部栏按钮
    pay_now: 'PAY NOW',
    add_to_order: 'ADD TO ORDER',
    add_to_cart: 'ADD TO CART',
    place_order: 'PLACE ORDER',
    view_order: 'VIEW ORDER',
    view_cart: 'VIEW CART',
    not_taking_orders: 'NOT TAKING ORDERS',
    submit: 'SUBMIT',
    select_a_size: 'SELECT A SIZE',

    // Common / 通用
    first_name: 'first name',

    // Dialog Buttons / 对话框按钮
    ok: 'OK',
    no_thanks: 'NO THANKS',
    yes: 'YES',
    no: 'NO',
    back: 'BACK',
    done: 'DONE',
    select: 'SELECT',
    upgrade_for: 'UPGRADE FOR',
    finish: 'FINISH',
    sign_up: 'SIGN UP',
    continue: 'CONTINUE',
    verify: 'VERIFY',
    enter_full_name: 'Enter your full name',
    enter_coupon_code: 'Enter coupon code',
    enter_member_number: 'Enter {name} number',
    coupon_not_found: 'We can\'t find that coupon code. Please try again.',
    coupon_conditions_not_met: 'You\'ve not met the coupon code conditions to receive a discount.',
    name_required: 'Name is required',
    code_required: 'Code is required',
    number_required: 'number is required',
    name_max_length: 'Name must be less than 40 characters',
    size: 'Size',
    confirm: 'CONFIRM',
    cancel: 'CANCEL',

    // Survey & Campaign / 调查和营销
    question: 'QUESTION',
    of: 'OF',
    offer_claimed: 'Offer claimed!',
    offer_sent_to: 'Details of the offer have been sent to',
    hope_see_you_soon: 'We hope to see you soon.',
    joined_mailing_list: 'You have successfully joined our mailing list.',
    oops: 'Oops!',
    something_went_wrong: 'Something went wrong. There was a problem submitting your answer.',
    thank_you_for_order: 'Thank you for your order!',
    please_answer_questions: 'Please take a moment to answer the following questions.',
    terms_and_conditions: 'Terms & conditions',
    join_mailing_list: 'Would you like to join our mailing list?',
    n_stars: '{n} stars',
    rating_description: 'Where 1 is poor and {max} is excellent',

    // Cancel Order Dialog / 取消订单对话框
    cancel_order_title: 'Cancel order?',
    cancel_order_confirm: 'Are you sure you want to cancel your order?',

    // Inactivity Dialog / 不活动对话框
    continue_order_title: 'Continue order?',
    continue_order_confirm: 'Do you still want to continue with this order?',

    // Language & Currency Selector / 语言和货币选择器
    select_language: 'Select Language',
    select_currency: 'Select Currency',

    // Contact Details / 联系方式
    contact_details: 'Contact Details',
    contact_first_name: 'First Name',
    contact_last_name: 'Last Name',
    email: 'Email',
    phone: 'Phone',
    phone_number: 'Phone Number',

    // Delivery / 配送
    delivery: 'Delivery',
    pickup: 'Pickup',
    dine_in: 'Dine In',
    takeaway: 'Takeaway',
    estimated_wait_time: 'Estimated wait time:',
    est_wait: 'Est. Wait:',
    mins: 'mins',
    estimated_delivery_time: 'Estimated delivery time',
    pickup_time: 'Pickup time',
    order_type: 'Order type:',
    deliver_to: 'Deliver to',
    pickup_from: 'Pickup from',
    pickup_time_unavailable: 'Pickup time unavailable',
    change_pickup_time: 'Please change your scheduled pickup time before continuing.',
    delivery_time_unavailable: 'Delivery time unavailable',
    change_delivery_time: 'Please change your scheduled delivery time before continuing.',

    // General / 通用
    order_history: 'Order history',
    order_history_description: 'You can view your previous orders from this venue here.',
    got_it: 'GOT IT',
    dismiss: 'DISMISS',
    terms_of_use: 'Terms of Use',
    privacy_statement: 'Privacy Statement',
    view_details: 'VIEW DETAILS',
    view_order_status: 'VIEW ORDER STATUS',
    previous_orders_not_visible: 'Have you placed previous orders you can\'t see here?',
    retrieve_full_order_history: 'RETRIEVE FULL ORDER HISTORY',
    no_order_history_title: 'No order history',
    no_orders_placed_yet: 'You haven\'t placed any orders yet.',
    ordered_from_venue_before: 'Ordered from this venue before?',
    retrieve_order_history: 'RETRIEVE FULL ORDER HISTORY',
    no_order_history_venue: 'You have no order history with this venue.',
    time_at: 'at',
    contains_label: 'Contains:',
    seafood_origin_australian: 'Australian',
    seafood_origin_imported: 'Imported',
    seafood_origin_mixed: 'Mixed Origin',
    loading: 'Loading...',
    error: 'Error',
    success: 'Success',
    items: 'items',
    item: 'item',
    cart_items_title: 'Items:',

    // Page Titles / 页面标题
    my_cart: 'My cart',
    select_payment_method: 'Select a payment method',
    card_details: 'Card details',
    tap_and_pay: 'Tap & Pay',

    // Cart Page Additional / 购物车页面附加
    table_number_zone: 'Table number/zone',
    add_a_note: 'Add a note',
    no_emojis_please: 'no emojis please',
    add_note_here: 'Add note here',
    settings: 'Settings',

    // Contact Details Page / 联系方式页面
    name: 'Name',
    email_address: 'Email address',
    required_to_identify: 'Required to identify your order & provide order updates.',
    not_used_for_marketing: 'It will not be used for marketing.',
    email_for_tax_receipt: 'Your email will only be used to send you a tax receipt.',

    // Payment Page / 支付页面附加
    start_a_tab: 'START A TAB',
    already_set_up_tab: 'Already set up a tab?',
    pay_with_tab: 'Pay with tab',
    next: 'NEXT',
    pay: 'PAY',
    amount_to_pay: 'Amount to pay:',
    add_a_tip: 'Add a tip?',
    would_you_like_to_add_tip: 'Would you like to add a tip?',
    no_tip: 'NO TIP',
    save_changes: 'Save Changes',

    // Card Details Page / 银行卡详情页面
    cvv: 'CVV',
    verification_process: 'Verification Process',

    // Order Confirmation Page / 订单确认页面
    payment_received: 'Payment received',
    order_received_title: 'Order received',
    place_another_order: 'PLACE ANOTHER ORDER',
    order_more: 'ORDER MORE',
    receipt_sent_to: 'Your receipt has been sent to {email}.',
    download_tax_invoice: 'Download tax invoice',

    // AI Recommendation / AI 推荐
    ai_rec_goes_well_with: 'Goes well with',
    ai_rec_you_may_also_like: 'You may also like',
    ai_rec_how_about_something: 'How about something?',
    ai_rec_recommended_for_you: 'Recommended for you',
    ai_rec_complete_your_meal: 'Complete your meal',
    ai_rec_perfect_pairing: 'Perfect pairing',
    ai_rec_a_refreshing_match: 'A refreshing match',
    ai_rec_great_with_your_order: 'Great with your order',
    ai_rec_a_tasty_extra: 'A tasty extra',
    ai_rec_similar_style: 'Similar style',
    ai_rec_same_cuisine: 'Same cuisine',
    ai_rec_similar_flavor: 'Similar flavor',

    // Menu changed (multi-version menus) / 菜单变了（多版本菜单 B6）—— 逐字照搬 kiosk defaults.ts:58、:144-147
    menu_just_updated: 'The menu was just updated. Please check your cart.',
    menu_updated_items_subtitle: 'These items are no longer available:',
    menu_updated_no_names: 'Some items in your cart are no longer available.',
    review_order: 'Review order',
    item_load_failed_hint: 'We couldn\'t load this item right now. Please try again.',

    // Menu preview (multi-version menus) / 菜单预览（多版本菜单 B6 U-B6-3）—— 逐字照搬 kiosk defaults.ts:136-137
    preview_orders_disabled: 'Preview — orders can\'t be placed',
    preview_ended: 'This preview has ended — showing the main menu. Ask for a new link to see it again.',
  },

  // French translations / 法文翻译
  fr: {
    // Menu Page / 菜单页面
    menu: 'Menu',
    sold_out: 'ÉPUISÉ',
    popular: 'Populaire',
    cancel_order: 'ANNULER LA COMMANDE',

    // Cart Page / 购物车页面
    your_order: 'Votre commande',
    subtotal: 'Sous-total',
    discount: 'Remise',
    order_total: 'Total de la commande',
    back_to_menu: 'RETOUR AU MENU',
    have_discount_code: 'AVEZ-VOUS UN CODE DE RÉDUCTION?',
    enter_discount_code: 'Entrez le code de réduction',
    invalid_discount_code: 'Code de réduction invalide',
    discount_applied: 'La réduction a été appliquée',
    public_holiday_surcharge: 'Supplément jour férié',
    weekday_surcharge: 'Supplément {weekDay}',
    weekdays: {
      monday: 'lundi',
      tuesday: 'mardi',
      wednesday: 'mercredi',
      thursday: 'jeudi',
      friday: 'vendredi',
      saturday: 'samedi',
      sunday: 'dimanche'
    },
    payment_surcharge: 'Supplément du lieu',
    enter_name_prompt: 'Entrez votre {nameType} pour être appelé lorsque votre commande est prête',
    edit: 'Modifier',
    remove: 'SUPPRIMER',
    add_label: 'Ajouter:',
    remove_label: 'Retirer:',
    add_something_extra: 'Ajouter quelque chose en plus:',
    cart: 'Panier',
    checkout: 'Commander',
    empty_cart: 'Votre panier est vide',
    continue_shopping: 'Continuer vos achats',
    // Currency estimate warning / 货币估算警告
    currency_estimate_warning: 'Les prix affichés en {currency} sont une estimation uniquement.',
    currency_charge_notice: 'Vous serez débité en {defaultCurrency}. Votre banque peut appliquer un taux de change et des frais différents.',

    // Age Verification / 年龄验证
    age_verification: 'Vérification de l\'âge',
    age_info:
      'Votre commande contient de l\'alcool. Veuillez confirmer que vous avez 18 ans ou plus. Vous devrez peut-être présenter une pièce d\'identité.',
    age_ask_text: 'Oui, j\'ai 18 ans ou plus',
    wrong_age_text:
      'Vous devez avoir 18 ans ou plus pour commander des produits contenant de l\'alcool. Veuillez retirer ces produits de votre panier avant de continuer.',

    // Payment Page / 支付页面
    payment: 'Paiement',
    payment_method: 'Mode de paiement',
    connecting_to: 'Connexion au',
    payment_terminal: 'terminal de paiement',
    please_follow_the: 'Veuillez suivre les',
    payment_instructions_on: 'instructions de paiement sur',
    the_terminal_below: 'le terminal ci-dessous',
    cancel_payment: 'ANNULER LE PAIEMENT',
    payment_failed: 'Échec du paiement',
    retry: 'RÉESSAYER',
    cancel_payment_title: 'Annuler le paiement?',
    cancel_payment_confirm: 'Êtes-vous sûr de vouloir annuler votre paiement?',
    failed_to_generate_order: 'Échec de la génération de commande',
    please_resubmit_order: 'Veuillez soumettre à nouveau la commande ou contacter l\'administrateur',
    resubmit: 'RESOUMETTRE',

    // Order Received Page / 订单确认页面
    thank_you: 'Merci!',
    order_received: 'Nous avons reçu votre commande',
    status: 'Statut:',
    order_details: 'Détails de la commande:',
    order_number: 'NUMÉRO DE COMMANDE',
    store_order_number: 'Numéro de commande',
    show_each_store_note: 'Présentez à chaque commerce son propre numéro au moment du retrait.',
    order_placed: 'Commande passée:',
    table_zone: 'Table/zone:',
    total_amount_paid: 'Montant total payé:',
    questions_about_order: 'Questions concernant votre commande?',
    scan_for_receipt: 'Scannez ici pour votre reçu',
    sms_receipt: 'REÇU PAR SMS',
    email_receipt: 'REÇU PAR EMAIL',
    total_inc_gst: 'Total (TTC)',
    close: 'FERMER',
    enter_phone_number: 'Entrez le numéro de téléphone local',
    enter_email_address: 'Entrez l\'adresse e-mail',

    // Product Detail Page / 产品详情页面
    item_added_to: 'Article ajouté à',
    required: 'Obligatoire',
    required_suffix: ' (Obligatoire)',
    optional: 'Optionnel',
    select_n: 'Sélectionnez {n}',
    select_n_required: 'Sélectionnez {n} (Obligatoire)',
    select_between_n_m: 'Sélectionnez entre {n} et {m}',
    select_between_n_m_required: 'Sélectionnez entre {n} et {m} (Obligatoire)',
    select_at_least_n: 'Sélectionnez au moins {n}',
    select_at_least_n_required: 'Sélectionnez au moins {n} (Obligatoire)',
    select_up_to_n: 'Sélectionnez jusqu\'à {n}',
    select_up_to_n_required: 'Sélectionnez jusqu\'à {n} (Obligatoire)',

    // Footer Bar Buttons / 底部栏按钮
    pay_now: 'PAYER MAINTENANT',
    add_to_order: 'AJOUTER À LA COMMANDE',
    add_to_cart: 'AJOUTER AU PANIER',
    place_order: 'PASSER LA COMMANDE',
    view_order: 'VOIR LA COMMANDE',
    view_cart: 'VOIR LE PANIER',
    not_taking_orders: 'NE PREND PAS DE COMMANDES',
    submit: 'SOUMETTRE',
    select_a_size: 'SÉLECTIONNER UNE TAILLE',

    // Common / 通用
    first_name: 'prénom',

    // Dialog Buttons / 对话框按钮
    ok: 'OK',
    no_thanks: 'NON MERCI',
    yes: 'OUI',
    no: 'NON',
    back: 'RETOUR',
    done: 'TERMINÉ',
    select: 'SÉLECTIONNER',
    upgrade_for: 'AMÉLIORER POUR',
    finish: 'TERMINER',
    sign_up: 'S\'INSCRIRE',
    continue: 'CONTINUER',
    verify: 'VÉRIFIER',
    enter_full_name: 'Entrez votre nom complet',
    enter_coupon_code: 'Entrez le code du coupon',
    enter_member_number: 'Entrez le numéro {name}',
    coupon_not_found: 'Nous ne trouvons pas ce code de coupon. Veuillez réessayer.',
    coupon_conditions_not_met: 'Vous n\'avez pas rempli les conditions du coupon pour bénéficier d\'une réduction.',
    name_required: 'Le nom est requis',
    code_required: 'Le code est requis',
    number_required: 'le numéro est requis',
    name_max_length: 'Le nom doit comporter moins de 40 caractères',
    size: 'Taille',
    confirm: 'CONFIRMER',
    cancel: 'ANNULER',

    // Survey & Campaign / 调查和营销
    question: 'QUESTION',
    of: 'DE',
    offer_claimed: 'Offre réclamée !',
    offer_sent_to: 'Les détails de l\'offre ont été envoyés à',
    hope_see_you_soon: 'Nous espérons vous revoir bientôt.',
    joined_mailing_list: 'Vous avez rejoint notre liste de diffusion avec succès.',
    oops: 'Oups !',
    something_went_wrong: 'Un problème est survenu lors de la soumission de votre réponse.',
    thank_you_for_order: 'Merci pour votre commande !',
    please_answer_questions: 'Veuillez prendre un moment pour répondre aux questions suivantes.',
    terms_and_conditions: 'Termes et conditions',
    join_mailing_list: 'Souhaitez-vous rejoindre notre liste de diffusion ?',
    n_stars: '{n} étoiles',
    rating_description: 'Où 1 est faible et {max} est excellent',

    // Cancel Order Dialog / 取消订单对话框
    cancel_order_title: 'Annuler la commande?',
    cancel_order_confirm: 'Êtes-vous sûr de vouloir annuler votre commande?',

    // Inactivity Dialog / 不活动对话框
    continue_order_title: 'Continuer la commande?',
    continue_order_confirm: 'Voulez-vous toujours continuer cette commande?',

    // Language & Currency Selector / 语言和货币选择器
    select_language: 'Sélectionner la langue',
    select_currency: 'Sélectionner la devise',

    // Contact Details / 联系方式
    contact_details: 'Coordonnées',
    contact_first_name: 'Prénom',
    contact_last_name: 'Nom',
    email: 'E-mail',
    phone: 'Téléphone',
    phone_number: 'Numéro de téléphone',

    // Delivery / 配送
    delivery: 'Livraison',
    pickup: 'À emporter',
    dine_in: 'Sur place',
    takeaway: 'À emporter',
    estimated_wait_time: 'Temps d\'attente estimé :',
    est_wait: 'Att. est. :',
    mins: 'min',
    estimated_delivery_time: 'Heure de livraison estimée',
    pickup_time: 'Heure de retrait',
    order_type: 'Type de commande :',
    deliver_to: 'Livrer à',
    pickup_from: 'Retrait à',
    pickup_time_unavailable: 'Heure de retrait indisponible',
    change_pickup_time: 'Veuillez modifier l\'heure de retrait prévue avant de continuer.',
    delivery_time_unavailable: 'Heure de livraison indisponible',
    change_delivery_time: 'Veuillez modifier l\'heure de livraison prévue avant de continuer.',

    // General / 通用
    order_history: 'Historique des commandes',
    order_history_description: 'Vous pouvez consulter vos commandes précédentes de ce restaurant ici.',
    got_it: 'COMPRIS',
    dismiss: 'FERMER',
    terms_of_use: 'Conditions d\'utilisation',
    privacy_statement: 'Politique de confidentialité',
    view_details: 'VOIR LES DÉTAILS',
    view_order_status: 'VOIR LE STATUT DE LA COMMANDE',
    previous_orders_not_visible: 'Avez-vous passé des commandes précédentes que vous ne voyez pas ici?',
    retrieve_full_order_history: 'RÉCUPÉRER L\'HISTORIQUE COMPLET',
    no_order_history_title: 'Aucun historique de commandes',
    no_orders_placed_yet: 'Vous n\'avez pas encore passé de commande.',
    ordered_from_venue_before: 'Vous avez déjà commandé dans ce restaurant ?',
    retrieve_order_history: 'RÉCUPÉRER L\'HISTORIQUE COMPLET',
    no_order_history_venue: 'Vous n\'avez aucun historique de commande avec ce restaurant.',
    time_at: 'à',
    contains_label: 'Contient :',
    seafood_origin_australian: 'Australien',
    seafood_origin_imported: 'Importé',
    seafood_origin_mixed: 'Origine mixte',
    loading: 'Chargement...',
    error: 'Erreur',
    success: 'Succès',
    items: 'articles',
    item: 'article',
    cart_items_title: 'Articles :',

    // Page Titles / 页面标题
    my_cart: 'Mon panier',
    select_payment_method: 'Sélectionner un mode de paiement',
    card_details: 'Détails de la carte',
    tap_and_pay: 'Tap & Pay',

    // Cart Page Additional / 购物车页面附加
    table_number_zone: 'Numéro de table/zone',
    add_a_note: 'Ajouter une note',
    no_emojis_please: 'pas d\'emojis svp',
    add_note_here: 'Ajouter une note ici',
    settings: 'Paramètres',

    // Contact Details Page / 联系方式页面
    name: 'Nom',
    email_address: 'Adresse e-mail',
    required_to_identify: 'Requis pour identifier votre commande et fournir des mises à jour.',
    not_used_for_marketing: 'Il ne sera pas utilisé à des fins de marketing.',
    email_for_tax_receipt: 'Votre e-mail ne sera utilisé que pour vous envoyer un reçu fiscal.',

    // Payment Page / 支付页面附加
    start_a_tab: 'OUVRIR UN COMPTE',
    already_set_up_tab: 'Vous avez déjà un compte?',
    pay_with_tab: 'Payer avec le compte',
    next: 'SUIVANT',
    pay: 'PAYER',
    amount_to_pay: 'Montant à payer :',
    add_a_tip: 'Ajouter un pourboire ?',
    would_you_like_to_add_tip: 'Souhaitez-vous ajouter un pourboire ?',
    no_tip: 'PAS DE POURBOIRE',
    save_changes: 'Enregistrer',

    // Card Details Page / 银行卡详情页面
    cvv: 'CVV',
    verification_process: 'Processus de vérification',

    // Order Confirmation Page / 订单确认页面
    payment_received: 'Paiement reçu',
    order_received_title: 'Commande reçue',
    place_another_order: 'PASSER UNE AUTRE COMMANDE',
    order_more: 'COMMANDER PLUS',
    receipt_sent_to: 'Votre reçu a été envoyé à {email}.',
    download_tax_invoice: 'Télécharger la facture fiscale',

    // AI Recommendation / AI 推荐
    ai_rec_goes_well_with: 'Se marie bien avec',
    ai_rec_you_may_also_like: 'Vous aimerez aussi',
    ai_rec_how_about_something: 'Et pourquoi pas ?',
    ai_rec_recommended_for_you: 'Recommandé pour vous',
    ai_rec_complete_your_meal: 'Complétez votre repas',
    ai_rec_perfect_pairing: 'Accord parfait',
    ai_rec_a_refreshing_match: 'Un accord rafraîchissant',
    ai_rec_great_with_your_order: 'Idéal avec votre commande',
    ai_rec_a_tasty_extra: 'Un petit extra savoureux',
    ai_rec_similar_style: 'Style similaire',
    ai_rec_same_cuisine: 'Même cuisine',
    ai_rec_similar_flavor: 'Saveur similaire',

    // Menu changed (multi-version menus) / 菜单变了（多版本菜单 B6）—— 逐字照搬 kiosk defaults.ts:517、:599-602
    menu_just_updated: 'Le menu vient d\'être mis à jour. Veuillez vérifier votre panier.',
    menu_updated_items_subtitle: 'Ces articles ne sont plus disponibles :',
    menu_updated_no_names: 'Certains articles de votre panier ne sont plus disponibles.',
    review_order: 'Vérifier la commande',
    item_load_failed_hint: 'Nous n\'avons pas pu charger cet article pour le moment. Veuillez réessayer.',

    // Menu preview (multi-version menus) / 菜单预览（多版本菜单 B6 U-B6-3）—— 逐字照搬 kiosk defaults.ts:592-593
    preview_orders_disabled: 'Aperçu — impossible de passer commande',
    preview_ended: 'Cet aperçu est terminé — le menu principal s\'affiche. Demandez un nouveau lien pour le revoir.',
  },

  // Chinese translations / 中文翻译
  zh: {
    // Menu Page / 菜单页面
    menu: '菜单',
    sold_out: '已售罄',
    popular: '热门',
    cancel_order: '取消订单',

    // Cart Page / 购物车页面
    your_order: '您的订单',
    subtotal: '小计',
    discount: '折扣',
    order_total: '订单总计',
    back_to_menu: '返回菜单',
    have_discount_code: '有优惠码吗？',
    enter_discount_code: '输入优惠码',
    invalid_discount_code: '优惠码无效',
    discount_applied: '折扣已应用',
    public_holiday_surcharge: '公共假期附加费',
    weekday_surcharge: '{weekDay}附加费',
    weekdays: {
      monday: '周一',
      tuesday: '周二',
      wednesday: '周三',
      thursday: '周四',
      friday: '周五',
      saturday: '周六',
      sunday: '周日'
    },
    payment_surcharge: '场所附加费',
    enter_name_prompt: '请输入您的{nameType}，订单准备好时我们会呼叫您',
    edit: '编辑',
    remove: '移除',
    add_label: '添加:',
    remove_label: '移除:',
    add_something_extra: '添加更多商品：',
    cart: '购物车',
    checkout: '结账',
    empty_cart: '购物车为空',
    continue_shopping: '继续购物',
    // Currency estimate warning / 货币估算警告
    currency_estimate_warning: '显示的{currency}价格仅为估算。',
    currency_charge_notice: '您将以{defaultCurrency}结算。您的银行可能会收取不同的汇率和手续费。',

    // Age Verification / 年龄验证
    age_verification: '年龄验证',
    age_info: '您的订单包含酒精饮品。请确认您已年满18岁。您可能需要向店员出示身份证明。',
    age_ask_text: '是的，我已满18岁',
    wrong_age_text: '您需要年满18岁才能订购含酒精的产品。请在继续之前从购物车中移除这些产品。',

    // Payment Page / 支付页面
    payment: '支付',
    payment_method: '支付方式',
    connecting_to: '正在连接',
    payment_terminal: '支付终端',
    please_follow_the: '请按照',
    payment_instructions_on: '支付终端上的',
    the_terminal_below: '说明进行操作',
    cancel_payment: '取消支付',
    payment_failed: '支付失败',
    retry: '重试',
    cancel_payment_title: '取消支付？',
    cancel_payment_confirm: '您确定要取消支付吗？',
    failed_to_generate_order: '订单生成失败',
    please_resubmit_order: '请重新提交订单或联系管理员',
    resubmit: '重新提交',

    // Order Received Page / 订单确认页面
    thank_you: '谢谢！',
    order_received: '我们已收到您的订单',
    status: '状态：',
    order_details: '订单详情：',
    order_number: '订单号',
    store_order_number: '订单号',
    show_each_store_note: '取餐时，请向每家店出示该店自己的单号。',
    order_placed: '下单时间：',
    table_zone: '桌号/区域：',
    total_amount_paid: '已付总额：',
    questions_about_order: '对订单有疑问？',
    scan_for_receipt: '扫描获取收据',
    sms_receipt: '短信收据',
    email_receipt: '邮件收据',
    total_inc_gst: '总计（含税）',
    close: '关闭',
    enter_phone_number: '输入本地电话号码',
    enter_email_address: '输入电子邮件地址',

    // Product Detail Page / 产品详情页面
    item_added_to: '已添加到',
    required: '必选',
    required_suffix: '（必选）',
    optional: '可选',
    select_n: '选择 {n} 项',
    select_n_required: '选择 {n} 项（必选）',
    select_between_n_m: '选择 {n} 到 {m} 项',
    select_between_n_m_required: '选择 {n} 到 {m} 项（必选）',
    select_at_least_n: '至少选择 {n} 项',
    select_at_least_n_required: '至少选择 {n} 项（必选）',
    select_up_to_n: '最多选择 {n} 项',
    select_up_to_n_required: '最多选择 {n} 项（必选）',

    // Footer Bar Buttons / 底部栏按钮
    pay_now: '立即支付',
    add_to_order: '添加到订单',
    add_to_cart: '加入购物车',
    place_order: '下单',
    view_order: '查看订单',
    view_cart: '查看购物车',
    not_taking_orders: '暂不接单',
    submit: '提交',
    select_a_size: '选择尺寸',

    // Common / 通用
    first_name: '名字',

    // Dialog Buttons / 对话框按钮
    ok: '确定',
    no_thanks: '不用了',
    yes: '是',
    no: '否',
    back: '返回',
    done: '完成',
    select: '选择',
    upgrade_for: '升级',
    finish: '完成',
    sign_up: '注册',
    continue: '继续',
    verify: '验证',
    enter_full_name: '请输入您的全名',
    enter_coupon_code: '请输入优惠码',
    enter_member_number: '请输入{name}号码',
    coupon_not_found: '找不到该优惠码，请重试。',
    coupon_conditions_not_met: '您未满足优惠码的折扣条件。',
    name_required: '请输入姓名',
    code_required: '请输入代码',
    number_required: '请输入号码',
    name_max_length: '姓名不能超过40个字符',
    size: '规格',
    confirm: '确认',
    cancel: '取消',

    // Survey & Campaign / 调查和营销
    question: '问题',
    of: '共',
    offer_claimed: '优惠已领取！',
    offer_sent_to: '优惠详情已发送至',
    hope_see_you_soon: '期待您的再次光临。',
    joined_mailing_list: '您已成功加入我们的邮件列表。',
    oops: '哎呀！',
    something_went_wrong: '提交您的回答时出现了问题。',
    thank_you_for_order: '感谢您的订单！',
    please_answer_questions: '请花一点时间回答以下问题。',
    terms_and_conditions: '条款与条件',
    join_mailing_list: '您想加入我们的邮件列表吗？',
    n_stars: '{n} 星',
    rating_description: '其中 1 为最差，{max} 为最佳',

    // Cancel Order Dialog / 取消订单对话框
    cancel_order_title: '取消订单？',
    cancel_order_confirm: '您确定要取消订单吗？',

    // Inactivity Dialog / 不活动对话框
    continue_order_title: '继续订单？',
    continue_order_confirm: '您还想继续这个订单吗？',

    // Language & Currency Selector / 语言和货币选择器
    select_language: '选择语言',
    select_currency: '选择货币',

    // Contact Details / 联系方式
    contact_details: '联系方式',
    contact_first_name: '名',
    contact_last_name: '姓',
    email: '邮箱',
    phone: '电话',
    phone_number: '电话号码',

    // Delivery / 配送
    delivery: '配送',
    pickup: '自取',
    dine_in: '堂食',
    takeaway: '外带',
    estimated_wait_time: '预计等待时间：',
    est_wait: '预计等待：',
    mins: '分钟',
    estimated_delivery_time: '预计送达时间',
    pickup_time: '取餐时间',
    order_type: '订单类型：',
    deliver_to: '配送至',
    pickup_from: '自取地点',
    pickup_time_unavailable: '取餐时间不可用',
    change_pickup_time: '请更改预定的取餐时间后再继续。',
    delivery_time_unavailable: '配送时间不可用',
    change_delivery_time: '请更改预定的配送时间后再继续。',

    // General / 通用
    order_history: '订单历史',
    order_history_description: '您可以在此查看您在本店的历史订单。',
    got_it: '知道了',
    dismiss: '关闭',
    terms_of_use: '使用条款',
    privacy_statement: '隐私声明',
    view_details: '查看详情',
    view_order_status: '查看订单状态',
    previous_orders_not_visible: '您之前的订单没有显示在这里吗？',
    retrieve_full_order_history: '获取完整订单历史',
    no_order_history_title: '暂无订单历史',
    no_orders_placed_yet: '您还没有下过任何订单。',
    ordered_from_venue_before: '之前在本店下过单吗？',
    retrieve_order_history: '获取完整订单历史',
    no_order_history_venue: '您在本店没有订单历史记录。',
    time_at: '于',
    contains_label: '含有：',
    seafood_origin_australian: '澳洲',
    seafood_origin_imported: '进口',
    seafood_origin_mixed: '混合产地',
    loading: '加载中...',
    error: '错误',
    success: '成功',
    items: '件商品',
    item: '件商品',
    cart_items_title: '商品：',

    // Page Titles / 页面标题
    my_cart: '我的购物车',
    select_payment_method: '选择支付方式',
    card_details: '银行卡详情',
    tap_and_pay: 'Tap & Pay',

    // Cart Page Additional / 购物车页面附加
    table_number_zone: '桌号/区域',
    add_a_note: '添加备注',
    no_emojis_please: '请勿使用表情符号',
    add_note_here: '在此添加备注',
    settings: '设置',

    // Contact Details Page / 联系方式页面
    name: '姓名',
    email_address: '电子邮箱',
    required_to_identify: '用于识别您的订单并提供订单更新。',
    not_used_for_marketing: '不会用于营销目的。',
    email_for_tax_receipt: '您的电子邮箱仅用于发送税务收据。',

    // Payment Page / 支付页面附加
    start_a_tab: '开启记账',
    already_set_up_tab: '已经开启记账了吗？',
    pay_with_tab: '使用记账支付',
    next: '下一步',
    pay: '支付',
    amount_to_pay: '应付金额：',
    add_a_tip: '添加小费？',
    would_you_like_to_add_tip: '您想要添加小费吗？',
    no_tip: '不需要小费',
    save_changes: '保存更改',

    // Card Details Page / 银行卡详情页面
    cvv: 'CVV',
    verification_process: '验证流程',

    // Order Confirmation Page / 订单确认页面
    payment_received: '支付成功',
    order_received_title: '订单已收到',
    place_another_order: '再次下单',
    order_more: '继续点餐',
    receipt_sent_to: '您的收据已发送至 {email}。',
    download_tax_invoice: '下载税务发票',

    // AI Recommendation / AI 推荐
    ai_rec_goes_well_with: '搭配推荐',
    ai_rec_you_may_also_like: '你可能还喜欢',
    ai_rec_how_about_something: '要不要来点别的？',
    ai_rec_recommended_for_you: '为你推荐',
    ai_rec_complete_your_meal: '搭配一下',
    ai_rec_perfect_pairing: '完美搭配',
    ai_rec_a_refreshing_match: '清爽搭配',
    ai_rec_great_with_your_order: '搭配你的点单',
    ai_rec_a_tasty_extra: '加点好吃的',
    ai_rec_similar_style: '相似风格',
    ai_rec_same_cuisine: '同系菜品',
    ai_rec_similar_flavor: '相近口味',

    // Menu changed (multi-version menus) / 菜单变了（多版本菜单 B6）—— 逐字照搬 kiosk defaults.ts:962、:1043-1046（半角逗号 / 冒号是 kiosk 原文）
    menu_just_updated: '菜单刚刚更新,请检查一下购物车。',
    menu_updated_items_subtitle: '以下商品已不可购买:',
    menu_updated_no_names: '购物车里有些商品已不可购买。',
    review_order: '检查订单',
    item_load_failed_hint: '暂时无法加载该商品，请重试。',

    // Menu preview (multi-version menus) / 菜单预览（多版本菜单 B6 U-B6-3）—— 逐字照搬 kiosk defaults.ts:1037-1038（半角逗号是 kiosk 原文）
    preview_orders_disabled: '预览中 —— 无法下单',
    preview_ended: '预览已结束,现在显示的是主菜单。如需再看,请向发链接给你的人要一个新的。',
  },

  // ── 只补「必选组组头」那三句的语言 / suffix-only packs ────────────────────
  //
  // 上面三份（en / fr / zh）是**整份** UI 文案。下面六份**只有三个键**，故意的：
  //
  // 门店真正用的九种语言（en / zh / fr / ja / ko / vi / th / es / ms）是管理端
  // 下发的（frontend-management `src/features/ui-translation/defaultTranslations.ts`），
  // 本仓这份只是「API 还没到 / 门店没自定义」时的内置底稿。所以整份翻译
  // **不该**在这里复制一份 —— 那会立刻和管理端分叉。
  //
  // 但 `required_suffix` / `select_at_least_n` / `select_at_least_n_required`
  // 这三个键**管理端一份都没有**（那九份里零出现）。于是这六种语言的门店，
  // 每一个必选配菜组的组头都会退到内联英文兜底 —— 顾客看得见的回归。
  // 在管理端把 239 个键补齐之前，这六份先把这三句顶上。
  //
  // 合并规则保证这么做是安全的（见本文件 `mergeLanguageSettings`）：
  // 按语言**浅合并、API 优先** —— API 下发的 ja 覆盖同名键，我们补的这三个键
  // 因为 API 那边没有，会原样活下来。少的键由 `t()` 逐键回退到默认语言，
  // 不会因为「这个语言块存在」而丢掉别的翻译。
  //
  // ⚠️ 这六份**不进** `ALL_AVAILABLE_LANGUAGES` / `DEFAULT_SUPPORTED_LANGUAGES`：
  // 那两个常量说的是「本仓自带完整翻译、可以直接开给门店选」的语言，
  // 三个键不够格。「提供哪些语言」由 API 的 supportedLanguages 决定，
  // 与这里加不加语言块无关。别顺手把它们加进去。
  //
  // 后缀的取值不是现造的，是**从管理端同语言的既有句子里减出来的**：
  //   `select_n_required` - `select_n` = 后缀
  // 于是组头（我们拼的）和红字（管理端下发的 `_required` 整句）标点逐字一致 ——
  // 这正是 2026-08-10 那次修复要解决的问题（中文门店两种括号）。
  // 括号形态各语言不同，是那份包自己的选择，不是这里定的：
  //   ja 与 zh 一样用全角、无前导空格；ko/vi/th/es/ms 用半角括号 + 前导空格。

  // Japanese / 日本語 —— 全角括号、无前导空格（与 zh 同，CJK 惯例）
  ja: {
    required_suffix: '（必須）',
    select_at_least_n: '{n}個以上選択',
    select_at_least_n_required: '{n}個以上選択（必須）',
  },

  // Korean / 한국어 —— 半角括号 + 前导空格（韩文分写，管理端包即如此）
  ko: {
    required_suffix: ' (필수)',
    select_at_least_n: '최소 {n}개 선택',
    select_at_least_n_required: '최소 {n}개 선택 (필수)',
  },

  // Vietnamese / Tiếng Việt —— 拉丁字母，半角括号 + 前导空格
  vi: {
    required_suffix: ' (Bắt buộc)',
    select_at_least_n: 'Chọn tối thiểu {n}',
    select_at_least_n_required: 'Chọn tối thiểu {n} (Bắt buộc)',
  },

  // Thai / ไทย —— 半角括号 + 前导空格（泰文本不分词，但管理端这份包通篇用空格分隔）
  th: {
    required_suffix: ' (จำเป็น)',
    select_at_least_n: 'เลือกอย่างน้อย {n}',
    select_at_least_n_required: 'เลือกอย่างน้อย {n} (จำเป็น)',
  },

  // Spanish / Español —— 拉丁字母，半角括号 + 前导空格
  es: {
    required_suffix: ' (Obligatorio)',
    select_at_least_n: 'Selecciona al menos {n}',
    select_at_least_n_required: 'Selecciona al menos {n} (Obligatorio)',
  },

  // Malay / Bahasa Melayu —— 拉丁字母，半角括号 + 前导空格
  ms: {
    required_suffix: ' (Wajib)',
    select_at_least_n: 'Pilih sekurang-kurangnya {n}',
    select_at_least_n_required: 'Pilih sekurang-kurangnya {n} (Wajib)',
  },
}

// ==================== Currency Settings / 货币设置 ====================

/**
 * Default supported currencies / 默认支持的货币
 *
 * By default, only Australian Dollar is enabled.
 * API can override this to enable multiple currencies.
 * 默认只启用澳大利亚元。
 * API 可以覆盖此设置以启用多种货币。
 */
export const DEFAULT_SUPPORTED_CURRENCIES = [{ code: 'AUD', symbol: '$', name: 'Australian Dollar', flag: '🇦🇺', countryCode: 'au', symbolPosition: 'before', decimalSeparator: '.' }]

/**
 * All available currencies / 所有可用的货币
 *
 * This is the complete list of currencies that can be enabled.
 * API can select which ones to enable for a specific venue.
 * 这是所有可以启用的货币完整列表。
 * API 可以选择为特定餐厅启用哪些货币。
 */
export const ALL_AVAILABLE_CURRENCIES = [
  { code: 'AUD', symbol: '$', name: 'Australian Dollar', flag: '🇦🇺', countryCode: 'au', symbolPosition: 'before', decimalSeparator: '.' },
  { code: 'USD', symbol: '$', name: 'US Dollar', flag: '🇺🇸', countryCode: 'us', symbolPosition: 'before', decimalSeparator: '.' },
  { code: 'EUR', symbol: '€', name: 'Euro', flag: '🇪🇺', countryCode: 'eu', symbolPosition: 'after', decimalSeparator: ',' },
  { code: 'GBP', symbol: '£', name: 'British Pound', flag: '🇬🇧', countryCode: 'gb', symbolPosition: 'before', decimalSeparator: '.' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan', flag: '🇨🇳', countryCode: 'cn', symbolPosition: 'before', decimalSeparator: '.' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen', flag: '🇯🇵', countryCode: 'jp', symbolPosition: 'before', decimalSeparator: '.' },
  { code: 'NZD', symbol: '$', name: 'New Zealand Dollar', flag: '🇳🇿', countryCode: 'nz', symbolPosition: 'before', decimalSeparator: '.' },
  { code: 'SGD', symbol: '$', name: 'Singapore Dollar', flag: '🇸🇬', countryCode: 'sg', symbolPosition: 'before', decimalSeparator: '.' },
  { code: 'HKD', symbol: '$', name: 'Hong Kong Dollar', flag: '🇭🇰', countryCode: 'hk', symbolPosition: 'before', decimalSeparator: '.' },
  { code: 'CAD', symbol: '$', name: 'Canadian Dollar', flag: '🇨🇦', countryCode: 'ca', symbolPosition: 'before', decimalSeparator: '.' },
  { code: 'MYR', symbol: 'RM', name: 'Malaysian Ringgit', flag: '🇲🇾', countryCode: 'my', symbolPosition: 'before', decimalSeparator: '.' },
  { code: 'THB', symbol: '฿', name: 'Thai Baht', flag: '🇹🇭', countryCode: 'th', symbolPosition: 'before', decimalSeparator: '.' },
]

/**
 * Default exchange rates (relative to venue's defaultCurrency)
 * 默认汇率（相对于餐厅的 defaultCurrency）
 *
 * These rates are used to convert prices from venue's base currency to display currency.
 * The venue's defaultCurrency always has a rate of 1.
 * 这些汇率用于将价格从餐厅的基础货币转换为显示货币。
 * 餐厅的 defaultCurrency 汇率始终为 1。
 *
 * Example / 示例:
 * - If venue's defaultCurrency is EUR and price is €10
 * - User selects USD (rate: 1.08)
 * - Display price: €10 * 1.08 = $10.80 USD
 *
 * These are fallback rates when API doesn't provide them.
 * API should provide accurate, up-to-date rates.
 * 这些是 API 未提供时的备用汇率。
 * API 应该提供准确、最新的汇率。
 */
export const DEFAULT_EXCHANGE_RATES = {
  AUD: 1,
  USD: 0.65,
  EUR: 0.6,
  GBP: 0.52,
  CNY: 4.72,
  JPY: 97.5,
  NZD: 1.08,
  SGD: 0.88,
  HKD: 5.08,
  CAD: 0.89,
  MYR: 2.86,
  THB: 23.62,
}

/**
 * Default language code / 默认语言代码
 */
export const DEFAULT_LANGUAGE = 'en'

/**
 * Default currency code / 默认货币代码
 */
export const DEFAULT_CURRENCY = 'AUD'

/**
 * Get complete default language settings
 * 获取完整的默认语言设置
 *
 * @returns {Object} Default language settings / 默认语言设置
 */
export function getDefaultLanguageSettings() {
  return {
    defaultLanguage: DEFAULT_LANGUAGE,
    supportedLanguages: DEFAULT_SUPPORTED_LANGUAGES,
    uiTranslations: DEFAULT_UI_TRANSLATIONS,
  }
}

/**
 * Get complete default currency settings
 * 获取完整的默认货币设置
 *
 * @returns {Object} Default currency settings / 默认货币设置
 */
export function getDefaultCurrencySettings() {
  return {
    defaultCurrency: DEFAULT_CURRENCY,
    supportedCurrencies: DEFAULT_SUPPORTED_CURRENCIES,
    exchangeRates: DEFAULT_EXCHANGE_RATES,
  }
}

/**
 * Merge API language settings with defaults
 * 将 API 语言设置与默认值合并
 *
 * API settings will override defaults for matching keys.
 * API 设置将覆盖匹配键的默认值。
 *
 * @param {Object} apiSettings - Language settings from API / API 返回的语言设置
 * @returns {Object} Merged language settings / 合并后的语言设置
 */
export function mergeLanguageSettings(apiSettings) {
  if (!apiSettings) {
    return getDefaultLanguageSettings()
  }

  const defaults = getDefaultLanguageSettings()

  // Merge supported languages - use API list if provided, otherwise use default
  // 合并支持的语言 - 如果 API 提供了列表则使用，否则使用默认
  const supportedLanguages = apiSettings.supportedLanguages?.length
    ? apiSettings.supportedLanguages
    : defaults.supportedLanguages

  // Merge UI translations - API translations override defaults per language
  // 合并 UI 翻译 - API 翻译按语言覆盖默认值
  const uiTranslations = { ...defaults.uiTranslations }
  if (apiSettings.uiTranslations) {
    Object.keys(apiSettings.uiTranslations).forEach(lang => {
      uiTranslations[lang] = {
        ...uiTranslations[lang],
        ...apiSettings.uiTranslations[lang],
      }
    })
  }

  return {
    defaultLanguage: apiSettings.defaultLanguage || defaults.defaultLanguage,
    supportedLanguages,
    uiTranslations,
  }
}

/**
 * Get default symbol position for a currency code
 * 获取货币代码的默认符号位置
 *
 * Used as a final fallback when neither API nor ALL_AVAILABLE_CURRENCIES
 * has the symbolPosition for a given currency.
 * 当 API 和 ALL_AVAILABLE_CURRENCIES 都没有给定货币的 symbolPosition 时作为最终回退。
 *
 * @param {string} currencyCode - Currency code (e.g. 'EUR', 'AUD') / 货币代码
 * @returns {string} 'before' or 'after' / 符号位置
 */
export function getDefaultSymbolPosition(currencyCode) {
  // Currencies where the symbol is placed after the number
  // 符号放在数字后面的货币
  const symbolAfterCurrencies = ['EUR']
  return symbolAfterCurrencies.includes(currencyCode?.toUpperCase()) ? 'after' : 'before'
}

/**
 * Get default decimal separator for a currency code
 * 获取货币代码的默认小数点分隔符
 *
 * Used as a final fallback when neither API nor ALL_AVAILABLE_CURRENCIES
 * has the decimalSeparator for a given currency.
 * 当 API 和 ALL_AVAILABLE_CURRENCIES 都没有给定货币的 decimalSeparator 时作为最终回退。
 *
 * @param {string} currencyCode - Currency code (e.g. 'EUR', 'AUD') / 货币代码
 * @returns {string} '.' or ',' / 小数点分隔符
 */
export function getDefaultDecimalSeparator(currencyCode) {
  // Currencies that use comma as decimal separator
  // 使用逗号作为小数点分隔符的货币
  const commaDecimalCurrencies = ['EUR']
  return commaDecimalCurrencies.includes(currencyCode?.toUpperCase()) ? ',' : '.'
}

/**
 * Merge API currency settings with defaults
 * 将 API 货币设置与默认值合并
 *
 * API settings will override defaults for matching keys.
 * API 设置将覆盖匹配键的默认值。
 *
 * @param {Object} apiSettings - Currency settings from API / API 返回的货币设置
 * @returns {Object} Merged currency settings / 合并后的货币设置
 */
export function mergeCurrencySettings(apiSettings) {
  if (!apiSettings) {
    return getDefaultCurrencySettings()
  }

  // Handle case where apiSettings is a string (just currency code)
  // 处理 apiSettings 是字符串（仅货币代码）的情况
  if (typeof apiSettings === 'string') {
    apiSettings = { defaultCurrency: apiSettings }
  }

  const defaults = getDefaultCurrencySettings()

  // Merge supported currencies - use API list if provided, otherwise use default
  // 合并支持的货币 - 如果 API 提供了列表则使用，否则使用默认
  // Enrich API currencies with missing fields (symbol, symbolPosition, decimalSeparator, flag, name) from ALL_AVAILABLE_CURRENCIES
  // 用 ALL_AVAILABLE_CURRENCIES 中的缺失字段（symbol, symbolPosition, decimalSeparator, flag, name）补充 API 货币
  let supportedCurrencies
  if (apiSettings.supportedCurrencies?.length) {
    supportedCurrencies = apiSettings.supportedCurrencies.map(apiCurr => {
      const builtIn = ALL_AVAILABLE_CURRENCIES.find(c => c.code === apiCurr.code)
      return {
        ...builtIn, // built-in defaults (symbol, symbolPosition, decimalSeparator, flag, name)
        ...apiCurr, // API values override
        // Ensure symbolPosition always has a value: API > built-in > fallback by code
        // 确保 symbolPosition 始终有值：API > 内置 > 按代码回退
        symbolPosition: apiCurr.symbolPosition || builtIn?.symbolPosition || getDefaultSymbolPosition(apiCurr.code),
        // Ensure decimalSeparator always has a value: API > built-in > fallback by code
        // 确保 decimalSeparator 始终有值：API > 内置 > 按代码回退
        decimalSeparator: apiCurr.decimalSeparator || builtIn?.decimalSeparator || getDefaultDecimalSeparator(apiCurr.code),
      }
    })
  } else {
    supportedCurrencies = defaults.supportedCurrencies
  }

  // Merge exchange rates - API rates override defaults
  // 合并汇率 - API 汇率覆盖默认值
  const exchangeRates = {
    ...defaults.exchangeRates,
    ...apiSettings.exchangeRates,
  }

  return {
    defaultCurrency: apiSettings.defaultCurrency || defaults.defaultCurrency,
    supportedCurrencies,
    exchangeRates,
  }
}
